import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Divider,
    Tooltip
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import supplierOrdersService from '../services/supplierOrdersService';
import suppliersService from '../services/suppliersService';

const SupplierOrderEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    // Order State
    const [order, setOrder] = useState({
        supplierId: '',
        status: 'draft',
        shippingCost: 0,
        additionalFees: 0,
        taxAmount: 0,
        expectedAt: '',
        notes: '',
        totalAmount: 0,
        items: []
    });

    const [suppliers, setSuppliers] = useState([]);

    // Add Item Dialog State
    const [openItemDialog, setOpenItemDialog] = useState(false);
    const [supplierProducts, setSupplierProducts] = useState([]); // Products available for this supplier
    const [loadingProducts, setLoadingProducts] = useState(false);

    const [itemForm, setItemForm] = useState({
        productId: null,
        productVariantId: null,
        quantity: 1,
        unitPrice: 0
    });
    const [selectedProduct, setSelectedProduct] = useState(null); // Used for Autocomplete

    const loadSupplierProducts = useCallback(async (supplierId) => {
        // We get the supplier details which includes products
        // Alternatively call a specific endpoint if list is huge
        try {
            setLoadingProducts(true);
            const supplier = await suppliersService.getSupplierById(supplierId);
            setSupplierProducts(supplier.supplierProducts || []);
        } catch (e) {
            console.error("Error loading supplier products", e);
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    const loadSuppliers = useCallback(async () => {
        try {
            const data = await suppliersService.getAllSuppliers();
            setSuppliers(data);
        } catch (e) {
            console.error("Error loading suppliers", e);
        }
    }, []);

    const loadOrder = useCallback(async () => {
        try {
            const data = await supplierOrdersService.getById(id);
            setOrder({
                ...data,
                expectedAt: data.expectedAt ? data.expectedAt.split('T')[0] : ''
            });
            // Also preload products for this supplier for the dialog
            loadSupplierProducts(data.supplierId);
        } catch (e) {
            console.error("Error loading order", e);
            alert("Erreur chargement commande");
            navigate('/supplier-orders');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, loadSupplierProducts]);

    useEffect(() => {
        loadSuppliers();
        if (!isNew) {
            loadOrder();
        }
    }, [id, isNew, loadSuppliers, loadOrder]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setOrder(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isNew) {
                const created = await supplierOrdersService.create({
                    supplierId: order.supplierId,
                    expectedAt: order.expectedAt,
                    notes: order.notes
                });
                navigate(`/supplier-orders/${created.id}`, { replace: true });
            } else {
                await supplierOrdersService.update(id, {
                    // Update main fields
                    status: order.status,
                    shippingCost: order.shippingCost,
                    additionalFees: order.additionalFees,
                    taxAmount: order.taxAmount,
                    expectedAt: order.expectedAt,
                    notes: order.notes
                });
                loadOrder(); // Refresh totals etc
                alert('Commande mise à jour');
            }
        } catch (e) {
            console.error("Error saving", e);
            alert("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    // --- Items Logic ---

    const handleOpenAddItem = () => {
        if (!order.supplierId) {
            alert("Veuillez d'abord sélectionner un fournisseur (et sauvegarder la commande)");
            return;
        }
        setOpenItemDialog(true);
        setItemForm({
            productId: null,
            productVariantId: null,
            quantity: 1,
            unitPrice: 0
        });
        setSelectedProduct(null);
    };

    const handleProductSelect = (event, value) => {
        // value is a supplierProduct object here (from supplierProducts list)
        // Check structure: supplierProducts item has { productId, productVariantId, price, product: {...}, variant: {...}, supplierReference }

        setSelectedProduct(value);
        if (value) {
            setItemForm(prev => ({
                ...prev,
                productId: value.productId,
                productVariantId: value.productVariantId,
                unitPrice: value.price // Default to valid price from supplier list
            }));
        }
    };

    const handleAddItemSubmit = async () => {
        try {
            if (!itemForm.quantity || itemForm.quantity <= 0) {
                alert("Quantité invalide");
                return;
            }
            // itemForm has IDs, but API needs them
            // Backend checks: "products sold by supplier". 
            // We selected from supplierProducts list so IDs should match.

            // Wait, selectedProduct is a SupplierProduct entry.
            // itemForm.productId comes from it.

            await supplierOrdersService.addItem(id, {
                productId: itemForm.productId,
                productVariantId: itemForm.productVariantId,
                quantity: itemForm.quantity,
                unitPrice: itemForm.unitPrice
            });

            setOpenItemDialog(false);
            loadOrder(); // Refresh items and totals
        } catch (e) {
            console.error("Error adding item", e);
            alert("Erreur lors de l'ajout");
        }
    };

    const handleRemoveItem = async (itemId) => {
        if (window.confirm("Retirer cet article ?")) {
            try {
                await supplierOrdersService.removeItem(id, itemId);
                loadOrder();
            } catch (e) {
                console.error("Error removing item", e);
            }
        }
    };


    const handleDownloadPdf = async () => {
        try {
            const blob = await supplierOrdersService.downloadPdf(id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `commande_fournisseur_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Erreur téléchargement PDF:', error);
            alert('Erreur lors du téléchargement du PDF');
        }
    };

    if (loading)
        return <LoadingSpinner />;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/supplier-orders')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    {isNew ? 'Nouvelle Commande Fournisseur' : `Commande #${order.id}`}
                </Typography>
                {!isNew && (
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<PdfIcon />}
                            onClick={handleDownloadPdf}
                            sx={{ mr: 2 }}
                        >
                            PDF
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </Box>
                )}
                {isNew && (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving || !order.supplierId}
                        sx={{ ml: 'auto' }}
                    >
                        Créer Brouillon
                    </Button>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Informations</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Fournisseur"
                                        name="supplierId"
                                        value={order.supplierId}
                                        onChange={handleChange}
                                        disabled={!isNew} // Cannot change supplier once created
                                        required
                                    >
                                        {suppliers.map(s => (
                                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Status"
                                        name="status"
                                        value={order.status}
                                        onChange={handleChange}
                                        disabled={isNew}
                                    >
                                        <MenuItem value="draft">Brouillon</MenuItem>
                                        <MenuItem value="ordered">Commandée</MenuItem>
                                        <MenuItem value="received">Reçue</MenuItem>
                                        <MenuItem value="cancelled">Annulée</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Date prévue"
                                        type="date"
                                        name="expectedAt"
                                        value={order.expectedAt || ''}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes"
                                        name="notes"
                                        multiline
                                        rows={3}
                                        value={order.notes || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {!isNew && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Coûts</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Frais de livraison"
                                            name="shippingCost"
                                            type="number"
                                            value={order.shippingCost}
                                            onChange={handleChange}
                                            InputProps={{ endAdornment: '€' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Frais supplémentaires"
                                            name="additionalFees"
                                            type="number"
                                            value={order.additionalFees}
                                            onChange={handleChange}
                                            InputProps={{ endAdornment: '€' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Taxes"
                                            name="taxAmount"
                                            type="number"
                                            value={order.taxAmount}
                                            onChange={handleChange}
                                            InputProps={{ endAdornment: '€' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="h6" align="right">
                                            Total: {order.totalAmount} €
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                <Grid item xs={12} md={8}>
                    {!isNew ? (
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6">Produits ({order.items?.length || 0})</Typography>
                                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddItem}>
                                        Ajouter Produit
                                    </Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center">Image</TableCell>
                                                <TableCell>Produit</TableCell>
                                                <TableCell>Variante</TableCell>
                                                <TableCell align="right">Qté</TableCell>
                                                <TableCell align="right">Prix U.</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(order.items || []).map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell align="center">
                                                        {(() => {
                                                            const product = item.product;
                                                            const image = product?.images?.find(i => i.isPrimary) || product?.images?.[0];
                                                            const imageUrl = image ? `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${image.url}` : null;
                                                            return imageUrl ? (
                                                                <Tooltip
                                                                    title={
                                                                        <Box
                                                                            component="img"
                                                                            src={imageUrl}
                                                                            alt={product?.name}
                                                                            sx={{
                                                                                maxWidth: 300,
                                                                                maxHeight: 300,
                                                                                objectFit: 'contain'
                                                                            }}
                                                                        />
                                                                    }
                                                                    arrow
                                                                    placement="right"
                                                                >
                                                                    <Box
                                                                        component="img"
                                                                        src={imageUrl}
                                                                        alt={product?.name}
                                                                        sx={{
                                                                            width: 40,
                                                                            height: 40,
                                                                            objectFit: 'contain',
                                                                            borderRadius: 1,
                                                                            cursor: 'zoom-in'
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            ) : (
                                                                <Box sx={{ width: 40, height: 40, bgcolor: 'grey.200', borderRadius: 1 }} />
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>{item.product?.name}</TableCell>
                                                    <TableCell>{item.variant?.name || '-'}</TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">{item.unitPrice} €</TableCell>
                                                    <TableCell align="right">{item.totalPrice} €</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton color="error" size="small" onClick={() => handleRemoveItem(item.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!order.items || order.items.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center">Aucun produit</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" align="center">
                                    Enregistrez la commande pour ajouter des produits.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>

            {/* DIALOG ADD ITEM */}
            <Dialog open={openItemDialog} onClose={() => setOpenItemDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Ajouter un produit</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            options={supplierProducts}
                            getOptionLabel={(option) => {
                                const pName = option.product?.name || 'Produit Inconnu';
                                const vName = option.variant ? ` - ${option.variant.name}` : '';
                                const ref = option.supplierReference ? ` (Ref: ${option.supplierReference})` : '';
                                return `${pName}${vName}${ref}`;
                            }}
                            renderOption={(props, option) => {
                                const product = option.product;
                                const image = product?.images?.find(i => i.isPrimary) || product?.images?.[0];
                                const imageUrl = image ? `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${image.url}` : null;
                                return (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            {imageUrl ? (
                                                <Box
                                                    component="img"
                                                    src={imageUrl}
                                                    alt={product?.name}
                                                    sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, flexShrink: 0 }}
                                                />
                                            ) : (
                                                <Box sx={{ width: 40, height: 40, bgcolor: 'grey.200', borderRadius: 1, flexShrink: 0 }} />
                                            )}
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.product?.name}
                                                    {option.variant && ` - ${option.variant.name}`}
                                                </Typography>
                                                {option.supplierReference && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Ref: {option.supplierReference}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </li>
                                );
                            }}
                            value={selectedProduct}
                            onChange={handleProductSelect}
                            renderInput={(params) => (
                                <TextField {...params} label="Sélectionner un produit du fournisseur" fullWidth />
                            )}
                        />
                        <TextField
                            label="Quantité"
                            type="number"
                            value={itemForm.quantity}
                            onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Prix Unitaire (Copie du catalogue)"
                            type="number"
                            value={itemForm.unitPrice}
                            onChange={(e) => setItemForm({ ...itemForm, unitPrice: e.target.value })}
                            fullWidth
                            InputProps={{ endAdornment: '€' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenItemDialog(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleAddItemSubmit}>Ajouter</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupplierOrderEdit;
